// Production policy (docs/PRODUCTION-POLICY.md): the API answers 503 `not_configured` with
// details { setting, adminPath, needs? } when a feature needs an outside account or key that the owner has not set up.
// The model gateway uses its own code, `provider_not_configured` (no details), for "no AI provider is enabled";
// a job that hits it ends with a `run.error` event carrying that code. Both map to the same NeedsSetup state.
import { ApiError } from './api';

export interface NotConfigured {
  message: string;
  setting: string;
  adminPath: string;
  needs?: string;
}

/** The admin portal origin, for "Set this up" links. */
export const ADMIN_ORIGIN = 'https://admin.crosspointhealth.org';

/** Error codes that mean "an outside account or key is missing", not "something broke". */
export const NOT_CONFIGURED_CODES: readonly string[] = ['not_configured', 'provider_not_configured'];

/** What `provider_not_configured` points at: Admin > AI + > Models and routing. */
export const MODEL_PROVIDER_SETUP: Omit<NotConfigured, 'message'> = {
  setting: 'AI models',
  adminPath: '/omni/models',
  needs: 'an API key from a model provider, entered in the admin portal',
};

const DEFAULT_PROVIDER_MESSAGE = 'No AI model provider is set up yet, so there is nothing to answer with.';

export const isNotConfiguredCode = (code: unknown): boolean => typeof code === 'string' && NOT_CONFIGURED_CODES.includes(code);

function fromParts(code: string, message: string | undefined, details: unknown): NotConfigured {
  const d = (details && typeof details === 'object' ? details : {}) as Partial<NotConfigured>;
  if (code === 'provider_not_configured' && !d.setting) {
    return { message: message || DEFAULT_PROVIDER_MESSAGE, ...MODEL_PROVIDER_SETUP };
  }
  return {
    message: message || 'This feature is not set up yet.',
    setting: d.setting ?? 'This feature',
    adminPath: d.adminPath ?? '/omni',
    ...(d.needs ? { needs: d.needs } : {}),
  };
}

/**
 * Returns the setup details when `err` is a not-configured error, else null. Accepts an `ApiError`, or a plain
 * `{ code, message, details? }` object (a `run.error` event's data, a folded job's `error`, a message's error part).
 */
export function asNotConfigured(err: unknown): NotConfigured | null {
  if (err instanceof ApiError) return isNotConfiguredCode(err.code) ? fromParts(err.code, err.message, err.details) : null;
  if (err && typeof err === 'object' && 'code' in err) {
    const e = err as { code?: unknown; message?: unknown; details?: unknown };
    if (!isNotConfiguredCode(e.code)) return null;
    return fromParts(e.code as string, typeof e.message === 'string' ? e.message : undefined, e.details);
  }
  return null;
}

/** True when the request never reached the API (offline, API down), as opposed to a typed 503 not_configured. */
export function isUnreachable(err: unknown): boolean {
  return err instanceof ApiError && err.isNetwork && !isNotConfiguredCode(err.code) && (err.code === 'network' || err.code.startsWith('http_') || err.status === 0);
}

export const adminUrl =(path: string) => `${ADMIN_ORIGIN}${path.startsWith('/') ? path : `/${path}`}`;
